import styles from "../styles/theme";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  const heading = { fontSize: 20, fontWeight: "bold", margin: "32px 0 12px", color: "#e8e4dc" };
  const subheading = { fontSize: 16, fontWeight: "bold", margin: "20px 0 8px", color: "#d4d0c8" };
  const para = { fontSize: 14, color: "#9a9490", lineHeight: 1.8, margin: "0 0 12px" };
  const list = { fontSize: 14, color: "#9a9490", lineHeight: 1.8, margin: "0 0 12px", paddingLeft: 24 };

  return (
    <div style={styles.root}>
      <Navbar view="privacy" setView={() => navigate("/")} navigate={navigate} />
      <main style={styles.main} data-main-container>
        <div style={{ maxWidth: 720, paddingTop: 32 }}>
          <button style={styles.backBtn} onClick={() => navigate("/")}>{"\u2190"} Back</button>
          <div style={{ background: "#111316", border: "1px solid #1e2022", borderRadius: 12, padding: "36px 40px" }}>
            <h1 style={{ fontSize: 28, fontWeight: "bold", margin: "0 0 8px", letterSpacing: "-0.5px" }}>Privacy Policy</h1>
            <p style={{ fontSize: 13, color: "#6a6660", margin: "0 0 28px" }}>Effective Date: May 16, 2026</p>

            <p style={para}>
              UXOHire ("we," "us," or "our") is committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the UXOHire platform ("Service"). Please read this policy carefully.
            </p>

            <h2 style={heading}>1. Information We Collect</h2>
            <h3 style={subheading}>1.1 Information You Provide</h3>
            <p style={para}>
              We collect information that you voluntarily provide when creating an account or using the Service, including:
            </p>
            <ul style={list}>
              <li><strong style={{ color: "#d4d0c8" }}>Account Information:</strong> Name, email address, and password.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Technician Profile Information:</strong> Phone number (optional), location (city and state), certifications held, field hours, employment history, qualifications, skills, and any additional information you choose to include in your profile.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Uploaded Documents:</strong> Resume, certifications, physical exam records, and other professional documents you choose to upload to your profile.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Document Metadata:</strong> Upload dates and expiration dates associated with your uploaded documents.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Employer Profile Information:</strong> Company name, contact name, email address, phone number, company website, and company description.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Job Posting Information:</strong> Job title, location, description, required and preferred certifications, compensation details, and other job-related information.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Invitation Records:</strong> Records of invitations sent by employers to technicians to apply for specific job postings.</li>
            </ul>

            <h3 style={subheading}>1.2 Information Collected Automatically</h3>
            <p style={para}>
              We use Vercel Analytics for page view and visitor tracking. Vercel Analytics collects anonymized, aggregated data about page views and visitor counts. It does not collect personal data, does not use cookies for tracking, and does not track individual users across sessions.
            </p>

            <h2 style={heading}>2. How We Use Your Information</h2>
            <p style={para}>
              We use the information we collect for the following purposes:
            </p>
            <ul style={list}>
              <li><strong style={{ color: "#d4d0c8" }}>Matching:</strong> To connect qualified UXO technicians with employers seeking personnel, which is the core function of the platform. Profile data including certifications and field hours are used to calculate job match percentages visible to employer accounts.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Platform Communications:</strong> To send you service-related notifications, including account verification, profile updates, job matches, and messages from employers or technicians.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Document Storage:</strong> Uploaded documents are stored securely and are only accessible to employers who have unlocked your profile through our paid unlock system.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Certification Alerts:</strong> Certification expiration dates are used to generate in-app notifications and alerts to help you maintain current credentials.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Service Improvement:</strong> To analyze usage patterns and improve the functionality, performance, and user experience of the platform.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Account Management:</strong> To manage your account, process subscriptions, and provide customer support.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
            </ul>

            <h2 style={heading}>3. Information Visibility</h2>
            <h3 style={subheading}>3.1 What Employers Can See (Without Unlock)</h3>
            <p style={para}>
              Employers with active subscriptions can browse technician profiles and view limited information, including certifications, general location, field hours, qualifications, and availability status. Contact details such as full name, email, and phone number are not visible without an unlock.
            </p>
            <h3 style={subheading}>3.2 What Employers Can See (After Unlock)</h3>
            <p style={para}>
              After an employer unlocks a technician's profile, they gain access to the technician's full contact information, including name, email address, and phone number. Employers can also view and download all documents uploaded by that technician. Unlocked information is subject to the usage restrictions described in our Terms of Service.
            </p>
            <h3 style={subheading}>3.3 Job Match Percentages</h3>
            <p style={para}>
              Employers can see job match percentages calculated from your certification and experience data. These percentages are estimates based on self-reported information and are intended to assist employers in evaluating potential candidates.
            </p>
            <h3 style={subheading}>3.4 Profile Status</h3>
            <p style={para}>
              Employer accounts can see your profile visibility status and Open to Work status.
            </p>

            <h2 style={heading}>4. Visibility Controls</h2>
            <p style={para}>
              UXOHire provides technicians with the following controls over their profile visibility:
            </p>
            <ul style={list}>
              <li><strong style={{ color: "#d4d0c8" }}>Profile Visibility:</strong> Technicians can hide their profile from other technicians on the platform.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Employer Visibility:</strong> Profiles are always visible to employer accounts when Open to Work status is enabled, regardless of technician-to-technician visibility settings.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Open to Work Status:</strong> Technicians can control their Open to Work status at any time through their profile settings.</li>
            </ul>

            <h2 style={heading}>5. Information We Do Not Sell</h2>
            <p style={para}>
              We do not sell, rent, or trade your personal information to third parties for their marketing purposes. We do not share your personal data with third parties except as described in this Privacy Policy or with your explicit consent.
            </p>

            <h2 style={heading}>6. Information Sharing</h2>
            <p style={para}>
              We may share your information only in the following limited circumstances:
            </p>
            <ul style={list}>
              <li><strong style={{ color: "#d4d0c8" }}>With Employers (via Unlock):</strong> When an employer unlocks your profile, your contact information is shared with that employer subject to our Terms of Service.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Service Providers:</strong> We may share information with third-party service providers who assist us in operating the platform (e.g., hosting, payment processing, email delivery), but only to the extent necessary for them to perform their services.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid legal process, such as a subpoena, court order, or government request.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Protection of Rights:</strong> We may disclose information to protect the rights, property, or safety of UXOHire, our users, or others.</li>
            </ul>

            <h2 style={heading}>7. Data Retention</h2>
            <p style={para}>
              Your profile and account data are kept active for as long as your account remains active. If you choose to close your account, your personal data will be deleted within 30 days of your request. Some information may be retained for longer periods as required by law or for legitimate business purposes, such as resolving disputes or enforcing our agreements.
            </p>
            <p style={para}>
              To request account closure and data deletion, please contact us at support@uxohire.com.
            </p>

            <h2 style={heading}>8. Cookies and Analytics</h2>
            <p style={para}>
              UXOHire uses Vercel Analytics for page view and visitor tracking. Vercel Analytics is a privacy-friendly analytics solution that:
            </p>
            <ul style={list}>
              <li>Does not collect personal data or personally identifiable information.</li>
              <li>Does not use cookies for tracking purposes.</li>
              <li>Does not track individual users across sessions or websites.</li>
              <li>Collects only anonymized, aggregated data about page views and visitor counts.</li>
            </ul>
            <p style={para}>
              We may use essential cookies strictly necessary for the operation of the platform, such as authentication session cookies. These cookies are not used for tracking or advertising purposes.
            </p>

            <h2 style={heading}>9. Data Security</h2>
            <p style={para}>
              We implement reasonable administrative, technical, and physical security measures to protect your personal information from unauthorized access, use, alteration, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 style={heading}>10. Your Rights</h2>
            <p style={para}>
              You have the following rights regarding your personal information:
            </p>
            <ul style={list}>
              <li><strong style={{ color: "#d4d0c8" }}>Access:</strong> You may request a copy of the personal information we hold about you.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Correction:</strong> You may update or correct your profile information at any time through the platform.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Deletion:</strong> You may request deletion of your personal data by closing your account.</li>
              <li><strong style={{ color: "#d4d0c8" }}>Portability:</strong> You may request a copy of your data in a portable format.</li>
            </ul>
            <p style={para}>
              To exercise any of these rights, please contact us at support@uxohire.com.
            </p>

            <h2 style={heading}>11. Children's Privacy</h2>
            <p style={para}>
              The Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we learn that we have collected personal information from a child under 18, we will take steps to delete that information promptly.
            </p>

            <h2 style={heading}>12. Changes to This Privacy Policy</h2>
            <p style={para}>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on the platform and updating the "Effective Date" above. Your continued use of the Service after the posting of changes constitutes your acceptance of such changes.
            </p>

            <h2 style={heading}>13. Governing Law</h2>
            <p style={para}>
              This Privacy Policy shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law principles.
            </p>

            <h2 style={heading}>14. Contact Us</h2>
            <p style={para}>
              If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:
            </p>
            <p style={{ ...para, color: "#d97706" }}>
              support@uxohire.com
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
